import _ from "lodash";
import React, { useState, useEffect, useCallback } from "react";
import WorkbookTemplate from "./WorkbookTemplate";
import { createAttachment } from "./modules/WorkbookTemplateGraphqlHelpers";
import { getCurrentCurriculumProgramType } from "modules/CommonSelectors";
import { getAttachment } from "modules/Services";
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";
import I18nHOC from "UIComponents/I18nHOC";
import Workbook from "AppComponents/Workbook";
import {
  WORKBOOK_MODE_TYPES,
  shouldEnableWorkbookTemplateLibrary,
} from "Utils";
import { Modal } from "@toddle-edu/ds-web";

const styles = {
  modalStyle: {
    padding: 0,
    marginTop: 0,
    marginBottom: 0,
  },
};

const omitKeys = [
  "metadata.oldWorkbookUrls",
  "metadata.updatedBy",
  "metadata.oldUrls",
  "metadata.fileExtension",
  "similarityReport",
  "parentType",
  "title",
  "streamUrl",
  "isRead",
  "questionAttachment",
  "__typename",
  "createdBy",
  "isAnnotationRead",
];

const WorkbookTemplateWrapper = props => {
  const {
    closeModal,
    onUpdateSuccessfully,
    updateAttachments,
    addAttachment,
    createAttachment,
    portalType,
    curriculumProgramType,
    workbookMode,
  } = props;

  const [useTemplate, setUseTemplate] = useState(false);
  const [templateAttachment, setTemplateAttachment] = useState({});
  const [attachment, setAttachment] = useState({});
  const [modalWidth, setModalWidth] = useState();
  const [templateFilters, setTemplateFilters] = useState({
    searchText: "",
    folderId: "allTemplates",
  });

  const updateWorkbookTemplateFilters = useCallback(data => {
    const { key, value } = data;
    setTemplateFilters(prevFilters => ({ ...prevFilters, [key]: value }));
  }, []);

  const getModalWidth = () => {
    const width = window.innerWidth;
    const cardWidth = 248; // 248 is the width of the card
    const modalMaxWidthAdjustment = 80;
    const widthWithoutCards = 260 + 40; // 260 is the sidebar width and 40 is the padding
    const numberOfCardsToRender = Math.floor(
      (width - widthWithoutCards - modalMaxWidthAdjustment) / cardWidth
    );
    const modalWidth = numberOfCardsToRender * cardWidth + widthWithoutCards;
    setModalWidth(modalWidth);
  };

  const throttledSetModalWidths = _.throttle(getModalWidth, 100);

  useEffect(() => {
    window.addEventListener("resize", throttledSetModalWidths);
    getModalWidth();

    return () => {
      window.removeEventListener("resize", throttledSetModalWidths);
    };
  }, []);

  const enableWorkbookTemplateLibrary = shouldEnableWorkbookTemplateLibrary({
    curriculumProgramType,
  });

  const onUpdate = async () => {
    const newAttachment = await getAttachment({
      id: attachment.id,
      portalType,
    });
    const tempArr = _.omit(newAttachment, omitKeys);

    addAttachment([tempArr]);
  };

  const createBlankWorkbook = async () => {
    const newAttachment = await createAttachment({
      attachment: {
        type: "WORKBOOK",
        name: "Workbook",
        metadata: {
          isWorkbookWithPageView: true,
          isNewBackgroundToolbarEnabled: true,
          isNewShapesToolbarEnabled: true,
          numOfPages: 1,
        },
      },
      portalType,
    });
    if (!_.isEmpty(newAttachment)) {
      setAttachment(newAttachment[0]);
      setUseTemplate(true);
    }
  };

  const handleCreateAttachment = useCallback(async () => {
    const template = templateAttachment;

    const templateChildAttachmentIds = _.get(
      template,
      "metadata.childAttachmentIds",
      []
    );

    const attachmentObject = {
      name: template?.name,
      type: template?.type,
      url: template?.url,
      thumbUrl: template?.thumbUrl,
      presetAttachmentId: template?.id,
      metadata: {
        ..._.omit(_.get(template, "metadata", {}), "childAttachmentIds"),
        templateChildAttachmentIds,
      },
    };

    const createdAttachment = await createAttachment({
      attachment: attachmentObject,
      portalType,
    });
    if (!_.isEmpty(createdAttachment)) {
      setAttachment(createdAttachment[0]);
      setUseTemplate(true);
    }
  }, [createAttachment, templateAttachment]);

  const computedWorkbookMode = workbookMode
    ? workbookMode
    : WORKBOOK_MODE_TYPES["WORKBOOK_MULTIPAGE_ADD_OR_EDIT"];

  const isAiTutorMode = _.startsWith(workbookMode, "AI_TUTOR_");

  return enableWorkbookTemplateLibrary && !isAiTutorMode ? (
    useTemplate ? (
      <Workbook
        attachment={attachment}
        workbookMode={computedWorkbookMode}
        onUpdateSuccessfully={onUpdate}
        onDiscardDirectly={closeModal}
        updateAttachments={updateAttachments}
        portalType={portalType}
      />
    ) : (
      <Modal onClose={closeModal} width={modalWidth}>
        <Modal.Body isScrollable={true} style={styles.modalStyle}>
          <WorkbookTemplate
            closeModal={closeModal}
            onUpdateSuccessfully={onUpdateSuccessfully}
            updateAttachments={updateAttachments}
            setUseTemplate={setUseTemplate}
            setTemplateAttachment={setTemplateAttachment}
            templateAttachment={templateAttachment}
            handleCreateAttachment={handleCreateAttachment}
            createBlankWorkbook={createBlankWorkbook}
            updateWorkbookTemplateFilters={updateWorkbookTemplateFilters}
            searchText={templateFilters.searchText}
            folderId={templateFilters.folderId}
            portalType={portalType}
          />
        </Modal.Body>
      </Modal>
    )
  ) : (
    <Workbook
      workbookMode={computedWorkbookMode}
      onUpdateSuccessfully={onUpdateSuccessfully}
      onDiscardDirectly={closeModal}
      updateAttachments={updateAttachments}
      portalType={portalType}
    />
  );
};

const mapStateToProps = (state, ownProps) => {
  const curriculumProgramType =
    getCurrentCurriculumProgramType(state) ||
    _.get(ownProps, "curriculumProgramType");

  return {
    curriculumProgramType,
  };
};

const mapActionCreators = {
  createAttachment,
  getAttachment,
};

export default compose(
  I18nHOC({ resource: ["common", "teacherHomePage"] }),
  connect(mapStateToProps, mapActionCreators)
)(WorkbookTemplateWrapper);
